import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import mediaService from '../api/mediaService';
import appInsights from '../services/appInsights';
import { useFavicon } from '../useFavicon';
import { useAuth } from '../contexts/AuthContext'; // Add this import
import '../App.css';

// Define the favicon for MediaFiles page
const MEDIA_FAVICON_URL = process.env.PUBLIC_URL + "/upload.svg"; // We'll create this icon

// Create a logger for the page
const logger = appInsights.getLogger('MediaFilesPage');

// Debug info about Application Insights state
console.log('MediaFilesPage - Application Insights State:', {
  initialized: appInsights.initialized,
  hasLogger: !!logger,
  loggerType: typeof logger,
  loggerMethods: logger ? Object.keys(logger) : 'none'
});

// Utility functions
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return 'Invalid Date';
  }
};

// Highlight matched text in a string
const highlightMatchedText = (text, query) => {
  if (!query || !text) return text;
  
  const lowerText = text.toString().toLowerCase();
  const lowerQuery = query.toLowerCase();
  
  if (!lowerText.includes(lowerQuery)) return text;
  
  const startIndex = lowerText.indexOf(lowerQuery);
  const endIndex = startIndex + lowerQuery.length;
  
  return (
    <>
      {text.toString().substring(0, startIndex)}
      <span style={{ 
        backgroundColor: '#FFFF00', 
        fontWeight: 'bold',
        padding: '0 2px'
      }}>
        {text.toString().substring(startIndex, endIndex)}
      </span>
      {text.toString().substring(endIndex)}
    </>
  );
};

/**
 * ResizableHeader component for table columns with sorting and resizing capabilities
 */
const ResizableHeader = ({ 
  children, 
  columnKey, 
  width, 
  onResize, 
  sortable = false, 
  sortField, 
  sortDirection,
  onSort 
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(width);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(width);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    e.preventDefault();
    
    const deltaX = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + deltaX); // Minimum 50px width
    onResize(columnKey, newWidth);
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add event listeners for mouse move and up
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, startX, startWidth]);

  const getSortIndicator = () => {
    if (!sortable || !sortDirection) return '';
    
    // Map columnKey to actual sortField values
    const actualSortField = sortField || columnKey;
    const currentSortField = sortField;
    
    // Check if this column is currently being sorted
    let isCurrentSortColumn = false;
    if (columnKey === 'name' && currentSortField === 'name') isCurrentSortColumn = true;
    else if (columnKey === 'type' && currentSortField === 'fileType') isCurrentSortColumn = true;
    else if (columnKey === 'size' && currentSortField === 'fileSize') isCurrentSortColumn = true;
    else if (columnKey === 'created' && currentSortField === 'createdAt') isCurrentSortColumn = true;
    else if (columnKey === 'description' && currentSortField === 'description') isCurrentSortColumn = true;
    else if (columnKey === 'id' && currentSortField === 'uniqueId') isCurrentSortColumn = true;
    
    if (!isCurrentSortColumn) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const handleHeaderClick = () => {
    if (sortable && onSort) {
      onSort(sortField || columnKey);
    }
  };

  return (
    <th 
      style={{ 
        width: `${width}px`,
        position: 'relative',
        minWidth: '50px',
        padding: '8px',
        borderRight: '1px solid #ddd',
        userSelect: 'none',
        cursor: sortable ? 'pointer' : 'default'
      }}
      onClick={handleHeaderClick}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        height: '100%'
      }}>
        <span>{children}{getSortIndicator()}</span>
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: isResizing ? '#007bff' : 'transparent',
            borderRight: '2px solid transparent',
            '&:hover': {
              backgroundColor: '#007bff'
            }
          }}
          onMouseDown={handleMouseDown}
        />
      </div>
    </th>
  );
};

/**
 * MediaFiles page - Displays a table of media files and allows uploading new files
 */
const MediaFilesPage = () => {
  useFavicon(MEDIA_FAVICON_URL); // Set the favicon for this page
  
  // Get authentication context for permission checking
  const { hasPermission } = useAuth();
  
  // Permission-based feature flags
  const canUpload = hasPermission('editor'); // Only editors and admins can upload
  const canDelete = hasPermission('editor');  // Editors can delete their own files, admins can delete any file
  const canEdit = hasPermission('editor');   // Only editors and admins can edit descriptions
  
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState(''); // Success message for general operations
  const [displayedQRCodes, setDisplayedQRCodes] = useState(new Set()); // Track which QR codes are currently displayed
  const [qrCodeDataUrls, setQRCodeDataUrls] = useState({}); // Cache QR code data URLs
  const [loadingQRCodes, setLoadingQRCodes] = useState(new Set()); // Track which QR codes are currently loading
  
  // Replace file functionality state
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replacingFileId, setReplacingFileId] = useState(null);
  const [replacingFileName, setReplacingFileName] = useState('');
  const [replaceFile, setReplaceFile] = useState(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering files
  const [sortField, setSortField] = useState('createdAt'); // Field to sort by
  const [sortDirection, setSortDirection] = useState('desc'); // Sort direction: 'asc' or 'desc'
  const [editingDescription, setEditingDescription] = useState(null); // Track which file's description is being edited
  const [editDescriptionValue, setEditDescriptionValue] = useState(''); // Current value of description being edited
  const [columnWidths, setColumnWidths] = useState({
    name: 200,
    type: 80,
    size: 100,
    created: 150,
    description: 200,
    id: 120,
    actions: 150
  }); // Column widths for resizing
  const searchInputRef = useRef(null);
  const navigate = useNavigate();  // Track page view and component lifecycle
  useEffect(() => {
    // Track component initialization
    logger.info('Media Files Page initializing', {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV
    });

    // Track page view with context
    appInsights.trackPageView('Media Files', window.location.pathname, {
      component: 'MediaFilesPage',
      timestamp: new Date().toISOString()
    });

    // Track component lifecycle
    appInsights.trackEvent('ComponentLifecycle', {
      component: 'MediaFilesPage',
      action: 'mount',
      timestamp: new Date().toISOString()
    });

    // Start data fetch
    fetchMediaFiles();

    // Cleanup on unmount
    return () => {
      logger.info('Media Files Page cleanup', {
        timestamp: new Date().toISOString(),
        component: 'MediaFilesPage'
      });

      appInsights.trackEvent('ComponentLifecycle', {
        component: 'MediaFilesPage',
        action: 'unmount',
        timestamp: new Date().toISOString()
      });
    };
  }, []);

  const fetchMediaFiles = async () => {
    const operationStart = Date.now();
    
    try {
      setIsLoading(true);
      
      // Track operation start - Verbose level for detailed operations
      logger.verbose('Fetching media files', {
        operation: 'fetchMediaFiles',
        timestamp: new Date().toISOString()
      });
      
      const data = await mediaService.getAllMediaFiles();
      setMediaFiles(data);
      setError(null);
      
      // Track successful operation - Info level for successful operations
      logger.info('Media files retrieved successfully', {
        operation: 'fetchMediaFiles',
        duration: Date.now() - operationStart,
        count: data.length,
        timestamp: new Date().toISOString()
      });
      
      // Track performance metrics
      appInsights.trackMetric('MediaFilesLoadTime', Date.now() - operationStart, {
        operation: 'fetchMediaFiles',
        success: true
      });
      
      // Track content metrics
      appInsights.trackMetric('MediaFilesCount', data.length, {
        operation: 'fetchMediaFiles'
      });
      
    } catch (err) {
      const errorMessage = 'Error fetching media files: ' + (err.message || 'Unknown error');
      setError(errorMessage);
      
      // Track error with full context - Error level for failures
      logger.error('Media files fetch failed', {
        operation: 'fetchMediaFiles',
        error: err.message,
        stack: err.stack,
        duration: Date.now() - operationStart,
        timestamp: new Date().toISOString()
      });
      
      // Track as exception with operation context
      appInsights.trackException({
        error: err,
        properties: {
          operation: 'fetchMediaFiles',
          duration: Date.now() - operationStart,
          component: 'MediaFilesPage'
        }
      });
      
    } finally {
      setIsLoading(false);
      
      // Track operation completion - Verbose level for operation details
      logger.verbose('Media files fetch operation completed', {
        operation: 'fetchMediaFiles',
        duration: Date.now() - operationStart,
        timestamp: new Date().toISOString()
      });
    }
  };
  // Handle file selection
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileInfo = {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      };
      
      // Track file selection - Info level for user actions
      logger.info('User selected file for upload', {
        operation: 'fileSelection',
        fileType: file.type,
        fileSize: formatFileSize(file.size),
        timestamp: new Date().toISOString()
      });

      // Validate file type
      const allowedTypes = ['audio/mp3', 'audio/mpeg', 'video/mp4', 'application/mp4', 'video/mpeg'];
      if (!allowedTypes.includes(file.type)) {
        const errorMsg = `Invalid file type: ${file.type}. Only MP3 and MP4 files are allowed.`;
        setError(errorMsg);
        
        // Track validation failure - Warn level for validation failures
        logger.warn('File type validation failed', {
          operation: 'fileValidation',
          invalidType: file.type,
          allowedTypes,
          timestamp: new Date().toISOString()
        });
        
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      // Validate file size
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        const errorMsg = `File is too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(maxSize)}.`;
        setError(errorMsg);
        
        // Track size validation failure - Warn level for validation failures
        logger.warn('File size validation failed', {
          operation: 'fileValidation',
          actualSize: file.size,
          maxSize,
          timestamp: new Date().toISOString()
        });
        
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      if (file.size === 0) {
        const errorMsg = 'File is empty (0 bytes)';
        setError(errorMsg);
        
        // Track empty file - Warn level for validation failures
        logger.warn('Empty file selected', {
          operation: 'fileValidation',
          fileName: file.name,
          timestamp: new Date().toISOString()
        });
        
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      // Verify file is readable
      try {
        // Track validation start - Verbose level for detailed operations
        logger.verbose('Starting file readability check', {
          operation: 'fileValidation',
          fileName: file.name,
          timestamp: new Date().toISOString()
        });        // Try to read first few bytes to verify file is accessible
        const slice = file.slice(0, 4);
        await new Response(slice).arrayBuffer();
          // Check for MP4 files in proxy environments
        if (file.type.includes('video') || file.type.includes('mp4')) {
          // Only treat known proxy signatures as corporate proxy indicators.
          const isProxyEnvironment = window.location.href.includes('shield.ericomcloud.net') ||
                                    window.location.origin.includes('ericomcloud.net') ||
                                    navigator.userAgent.includes('Shield') ||
                                    navigator.userAgent.includes('Ericom') ||
                                    document.referrer.includes('ericomcloud.net');
          
          if (isProxyEnvironment) {
            logger.info('MP4 file detected in proxy environment - will use base64 upload', {
              operation: 'fileValidation',
              fileType: file.type,
              fileSize: formatFileSize(file.size),
              environment: 'proxy',
              uploadMethod: 'base64',
              timestamp: new Date().toISOString()
            });
            
            // Show informative message about enhanced MP4 handling
            if (file.size > 5 * 1024 * 1024) {
              setError('🎬 Large MP4 file detected in corporate proxy environment. The system will automatically use a proxy-compatible base64 upload method for optimal compatibility.');
            } else {
              setError('🎬 MP4 file detected in corporate proxy environment. The system will automatically use a proxy-compatible upload method for optimal compatibility.');
            }
            
            // Clear informational message after 7 seconds
            setTimeout(() => {
              setError(null);
            }, 7000);
          }
        }
        
        setSelectedFile(file);
        // Default the name to the file name without extension if not already set
        if (!fileName) {
          const name = file.name.split('.').slice(0, -1).join('.');
          setFileName(name);
        }
        
        // Track successful validation - Info level for successful operations
        logger.info('File validation successful', {
          operation: 'fileValidation',
          fileName: file.name,
          fileType: file.type,
          fileSize: formatFileSize(file.size),
          timestamp: new Date().toISOString()
        });
        
      } catch (error) {
        const errorMsg = 'Unable to read file. Please try again.';
        setError(errorMsg);
        
        // Track file read error - Error level for operational failures
        logger.error('File read validation failed', {
          operation: 'fileValidation',
          fileName: file.name,
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });
        
        setSelectedFile(null);
        event.target.value = '';
      }
    }
  };
  // Handle file upload submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    const operationStart = Date.now();
    
    // Track upload attempt
    logger.info('File upload initiated', {
      operation: 'fileUpload',
      timestamp: new Date().toISOString()
    });
    
    if (!selectedFile) {
      const errorMsg = 'Please select a file to upload';
      setError(errorMsg);
      
      // Track validation failure - Warn level for validation failures
      logger.warn('Upload attempted without file selection', {
        operation: 'fileUpload',
        timestamp: new Date().toISOString()
      });
      return;
    }

    if (!fileName.trim()) {
      const errorMsg = 'Please provide a name for the file';
      setError(errorMsg);
      
      // Track validation failure - Warn level for validation failures
      logger.warn('Upload attempted without file name', {
        operation: 'fileUpload',
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Verify file is still valid before upload
    if (selectedFile.size === 0) {
      const errorMsg = 'File is empty. Please select a valid file.';
      setError(errorMsg);
      
      // Track validation failure - Warn level for validation failures
      logger.warn('Upload attempted with empty file', {
        operation: 'fileUpload',
        fileName: selectedFile.name,
        timestamp: new Date().toISOString()
      });
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      // Track upload start - Info level for operation start
      logger.info('Starting file upload', {
        operation: 'fileUpload',
        fileName: selectedFile.name,
        fileSize: formatFileSize(selectedFile.size),
        fileType: selectedFile.type,
        timestamp: new Date().toISOString()
      });      const uploadResult = await mediaService.uploadMediaFile(selectedFile, fileName, fileDescription);
      
      // Track successful upload - Info level for successful operations
      logger.info('File upload completed successfully', {
        operation: 'fileUpload',
        fileName: selectedFile.name,
        fileSize: formatFileSize(selectedFile.size),
        uploadMethod: uploadResult?.uploadMethod || 'standard',
        proxyWorkaround: uploadResult?.proxyWorkaround || false,
        duration: Date.now() - operationStart,
        timestamp: new Date().toISOString()
      });
      
      // Track upload performance
      appInsights.trackMetric('FileUploadDuration', Date.now() - operationStart, {
        operation: 'fileUpload',
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        uploadMethod: uploadResult?.uploadMethod || 'standard'
      });
      
      // Reset form
      setSelectedFile(null);
      setFileName('');
      setFileDescription('');
      
      // Reset file input
      const fileInput = document.getElementById('file');
      if (fileInput) fileInput.value = '';
      
      // Handle proxy workaround responses with special messaging
      if (uploadResult?.proxyWorkaround || uploadResult?.successMessage) {
        setUploadSuccess(true);
        
        // Show custom success message for proxy environments
        if (uploadResult.successMessage) {
          setError(null); // Clear any previous errors
          
          // Set a custom success message that includes the refresh instruction
          const successDiv = document.createElement('div');
          successDiv.className = 'success-message proxy-success';
          successDiv.innerHTML = `
            <div style="color: #28a745; background: #d4edda; padding: 12px; border: 1px solid #c3e6cb; border-radius: 4px; margin: 10px 0;">
              <strong>✅ ${uploadResult.successMessage}</strong>
              ${uploadResult.uploadMethod === 'base64' ? '<br/><small>💡 Your MP4 file was uploaded using a proxy-compatible method.</small>' : ''}
            </div>
          `;
          
          // Insert the message before the upload form
          const uploadForm = document.querySelector('form');
          if (uploadForm && uploadForm.parentNode) {
            uploadForm.parentNode.insertBefore(successDiv, uploadForm);
            
            // Remove the message after 10 seconds (longer for proxy messages)
            setTimeout(() => {
              if (successDiv.parentNode) {
                successDiv.parentNode.removeChild(successDiv);
              }
            }, 10000);
          }
        }
        
        // Don't auto-refresh in proxy environments since the response might be intercepted
        console.log('🔄 Proxy environment detected - skipping automatic media list refresh');
        
        // Clear success state after showing the message
        setTimeout(() => {
          setUploadSuccess(false);
        }, 5000);
      } else {
        // Normal upload success - refresh the list
        setUploadSuccess(true);
        await fetchMediaFiles();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000);
      }
    } catch (err) {
      const errorMsg = 'Error uploading file: ' + (err.message || 'Unknown error');
      setError(errorMsg);
      
      // Track upload failure - Error level for operation failures
      logger.error('File upload failed', {
        operation: 'fileUpload',
        fileName: selectedFile.name,
        error: err.message,
        stack: err.stack,
        duration: Date.now() - operationStart,
        timestamp: new Date().toISOString()
      });
      
      // Track as exception with full context
      appInsights.trackException({
        error: err,
        properties: {
          operation: 'fileUpload',
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          duration: Date.now() - operationStart
        }
      });
    } finally {
      setIsUploading(false);
      
      // Track operation completion - Verbose level for operation details
      logger.verbose('File upload operation completed', {
        operation: 'fileUpload',
        duration: Date.now() - operationStart,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking on the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking on a new field, set it as sort field with descending order
      setSortField(field);
      setSortDirection('desc');
    }
    
    // Log sorting action
    logger.info('User sorted media files table', {
      operation: 'sortTable',
      field: field,
      direction: sortField === field ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'desc',
      timestamp: new Date().toISOString()
    });
  };

  const sortFiles = (files) => {
    return [...files].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'fileType':
          aValue = a.fileType.toLowerCase();
          bValue = b.fileType.toLowerCase();
          break;
        case 'fileSize':
          aValue = a.fileSize || 0;
          bValue = b.fileSize || 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        case 'uniqueId':
          aValue = a.uniqueId.toLowerCase();
          bValue = b.uniqueId.toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Column resizing functions
  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(50, newWidth) // Minimum width of 50px
    }));
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) {
      return <span style={{ color: '#ccc', marginLeft: '5px' }}>↕️</span>;
    }
    return (
      <span style={{ marginLeft: '5px', color: '#007bff' }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Event handlers
  const handleViewFile = (fileId) => {
    logger.info('User navigating to view media file', {
      operation: 'viewFile',
      fileId: fileId,
      timestamp: new Date().toISOString()
    });
    navigate(`/view-media/${fileId}`);
  };

  const handleDeleteFile = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      try {
        setIsLoading(true);
        await mediaService.deleteMediaFile(fileId);
        
        // Remove from local state
        setMediaFiles(prevFiles => prevFiles.filter(file => file.uniqueId !== fileId));
        
        logger.info('File deleted successfully', {
          operation: 'deleteFile',
          fileId: fileId,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        setError(`Error deleting file: ${error.message}`);
        logger.error('Error deleting file', {
          operation: 'deleteFile',
          fileId: fileId,
          error: error.message,
          timestamp: new Date().toISOString()
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleReplaceFile = (fileId, fileName) => {
    setReplacingFileId(fileId);
    setReplacingFileName(fileName);
    setReplaceFile(null);
    setReplaceDialogOpen(true);
    
    logger.info('Replace file dialog opened', {
      operation: 'openReplaceDialog',
      fileId: fileId,
      fileName: fileName,
      timestamp: new Date().toISOString()
    });
  };

  const handleConfirmReplace = async () => {
    if (!replaceFile || !replacingFileId) {
      setError('Please select a file to replace with');
      return;
    }

    try {
      setIsReplacing(true);
      setError(null);
      setSuccessMessage('');

      logger.info('Starting file replacement', {
        operation: 'replaceFile',
        originalFileId: replacingFileId,
        newFileName: replaceFile.name,
        newFileSize: formatFileSize(replaceFile.size),
        timestamp: new Date().toISOString()
      });

      // Call the replace API
      const result = await mediaService.replaceMediaFile(replacingFileId, replaceFile);
      
      logger.info('File replaced successfully', {
        operation: 'replaceFile',
        originalFileId: replacingFileId,
        newFileName: replaceFile.name,
        result: result,
        timestamp: new Date().toISOString()
      });

      // Update the local state with the new file data
      setMediaFiles(prevFiles => 
        prevFiles.map(file => 
          file.uniqueId === replacingFileId 
            ? { ...file, ...result, name: replaceFile.name }
            : file
        )
      );

      // Show success message and close dialog after a brief delay
      setSuccessMessage(`File "${replaceFile.name}" replaced successfully!`);
      setTimeout(() => {
        setReplaceDialogOpen(false);
        setReplacingFileId(null);
        setReplacingFileName('');
        setReplaceFile(null);
        setSuccessMessage('');
      }, 2000);
      
    } catch (error) {
      setError(`Error replacing file: ${error.message}`);
      logger.error('Error replacing file', {
        operation: 'replaceFile',
        originalFileId: replacingFileId,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsReplacing(false);
    }
  };

  const handleDownloadQRCode = async (fileId, fileName) => {
    try {
      await mediaService.downloadQRCode(fileId, fileName);
      logger.info('QR code downloaded successfully', {
        operation: 'downloadQRCode',
        fileId: fileId,
        fileName: fileName,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setError(`Error downloading QR code: ${error.message}`);
      logger.error('Error downloading QR code', {
        operation: 'downloadQRCode',
        fileId: fileId,
        fileName: fileName,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleToggleQRCode = async (fileId) => {
    if (displayedQRCodes.has(fileId)) {
      // Hide QR code
      setDisplayedQRCodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
      
      // Remove from cache
      setQRCodeDataUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[fileId];
        return newUrls;
      });
    } else {
      // Show QR code
      if (!qrCodeDataUrls[fileId] && !loadingQRCodes.has(fileId)) {
        try {
          setLoadingQRCodes(prev => new Set(prev).add(fileId));
          const qrCodeData = await mediaService.getQRCodeDataURL(fileId);
          
          setQRCodeDataUrls(prev => ({
            ...prev,
            [fileId]: qrCodeData
          }));
        } catch (error) {
          setError(`Error loading QR code: ${error.message}`);
          logger.error('Error loading QR code', {
            operation: 'loadQRCode',
            fileId: fileId,
            error: error.message,
            timestamp: new Date().toISOString()
          });
        } finally {
          setLoadingQRCodes(prev => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
          });
        }
      }
      
      setDisplayedQRCodes(prev => new Set(prev).add(fileId));
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  // Copy URL to clipboard handler
  const handleCopyUrl = async (fileId) => {
    try {
      const qrCodeData = qrCodeDataUrls[fileId];
      if (!qrCodeData?.frontendBaseUrl) {
        setError('QR code URL not available');
        return;
      }

      const url = `${qrCodeData.frontendBaseUrl.replace(/\/$/, '')}/view-media/${fileId}`;
      await navigator.clipboard.writeText(url);
      setSuccessMessage('URL copied to clipboard!');
      
      logger.info('URL copied to clipboard', {
        operation: 'copyUrl',
        fileId: fileId,
        timestamp: new Date().toISOString()
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      setError('Failed to copy URL to clipboard');
      logger.error('Error copying URL to clipboard', {
        operation: 'copyUrl',
        fileId: fileId,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Description editing handlers
  const handleEditDescription = (fileId, currentDescription) => {
    setEditingDescription(fileId);
    setEditDescriptionValue(currentDescription || '');
  };

  const handleSaveDescription = async (fileId) => {
    try {
      setIsLoading(true);
      
      logger.info('Updating media file description', {
        operation: 'updateDescription',
        fileId: fileId,
        newDescriptionLength: editDescriptionValue.length
      });

      await mediaService.updateMediaFileDescription(fileId, editDescriptionValue);
      
      // Update the local state
      setMediaFiles(prevFiles => 
        prevFiles.map(file => 
          file.uniqueId === fileId 
            ? { ...file, description: editDescriptionValue }
            : file
        )
      );
      
      // Clear editing state
      setEditingDescription(null);
      setEditDescriptionValue('');
      
      logger.info('Media file description updated successfully', {
        operation: 'updateDescription-success',
        fileId: fileId
      });
      
      setSuccessMessage('Description updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      logger.error('Failed to update media file description', {
        operation: 'updateDescription-error',
        fileId: fileId,
        error: error.message
      });
      
      setError(`Failed to update description: ${error.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEditDescription = () => {
    setEditingDescription(null);
    setEditDescriptionValue('');
  };

  // Filtered and sorted media files based on search term
  const filteredMediaFiles = sortFiles(mediaFiles.filter(file => {
    const term = searchTerm.toLowerCase();
    return (
      file.name.toLowerCase().includes(term) ||
      file.fileType.toLowerCase().includes(term) ||
      formatFileSize(file.fileSize).toLowerCase().includes(term) ||
      formatDate(file.createdAt).toLowerCase().includes(term) ||
      (file.description ? file.description.toLowerCase().includes(term) : false) ||
      file.uniqueId.toLowerCase().includes(term)
    );
  }));

  // Add keyboard shortcut to focus search field
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if Ctrl+F or Cmd+F was pressed
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        // Prevent the browser's find dialog
        event.preventDefault();
        
        // Focus the search input if it exists
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          
          // Track keyboard shortcut usage
          logger.info('User used keyboard shortcut for search', {
            operation: 'searchShortcut',
            timestamp: new Date().toISOString()
          });
        }
      }
    };
    
    // Add event listener for keydown
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="page-container">
      {/* CSS Styles for sortable and resizable table */}
      <style jsx>{`
        .media-table {
          border-collapse: separate;
          border-spacing: 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          border-radius: 8px;
          overflow: hidden;
        }
        
        .media-table th {
          border-right: 1px solid #dee2e6;
        }
        
        .media-table th:last-child {
          border-right: none;
        }
        
        .media-table td {
          border-right: 1px solid #f1f3f4;
          border-bottom: 1px solid #f1f3f4;
        }
        
        .media-table td:last-child {
          border-right: none;
        }
        
        .media-table tbody tr:last-child td {
          border-bottom: none;
        }
        
        .media-table tbody tr:hover {
          background-color: #f8f9fa;
        }
        
        .resize-handle:hover {
          background-color: rgba(0, 123, 255, 0.1) !important;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .loading-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
          vertical-align: middle;
        }
        
        .description-edit-container {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 100%;
        }
        
        .description-edit-textarea {
          width: 100%;
          min-height: 60px;
          padding: 4px;
          border: 1px solid #007BFF;
          border-radius: 4px;
          font-size: 12px;
          resize: vertical;
          font-family: inherit;
        }
        
        .description-edit-buttons {
          display: flex;
          gap: 4px;
        }
        
        .description-edit-save {
          padding: 2px 8px;
          font-size: 11px;
          background-color: #28a745;
          color: white;
          border: none;
          border-radius: 3px;
          cursor: pointer;
        }
        
        .description-edit-save:hover {
          background-color: #218838;
        }
        
        .description-edit-cancel {
          padding: 2px 8px;
          font-size: 11px;
          background-color: #6c757d;
          color: white;
          border: none;
          border-radius: 3px;
          cursor: pointer;
        }
        
        .description-edit-cancel:hover {
          background-color: #5a6268;
        }
        
        .description-display {
          display: flex;
          align-items: center;
          gap: 4px;
          overflow: hidden;
        }
        
        .description-text {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .description-edit-button {
          padding: 2px 6px;
          font-size: 11px;
          background-color: #007BFF;
          color: white;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          flex-shrink: 0;
        }
        
        .description-edit-button:hover {
          background-color: #0056b3;
        }
        
        .replace-button {
          background-color: #fd7e14;
          color: white;
          border: none;
          padding: 5px 10px;
          margin: 2px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: background-color 0.2s;
        }
        
        .replace-button:hover {
          background-color: #e8660c;
        }
        
        .replace-button:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 8px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .file-input-container {
          margin: 15px 0;
        }
        
        .file-preview {
          margin-top: 10px;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .modal-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 20px;
        }
        
        .cancel-button, .confirm-button {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }
        
        .cancel-button {
          background-color: #6c757d;
          color: white;
        }
        
        .cancel-button:hover {
          background-color: #545b62;
        }
        
        .confirm-button {
          background-color: #fd7e14;
          color: white;
        }
        
        .confirm-button:hover {
          background-color: #e8660c;
        }
        
        .confirm-button:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
      `}</style>
      
      <h1>Media Files</h1>
      
      {/* Upload Form */}
      <div className="upload-section">
        <h2>Upload New Media File</h2>
        <form onSubmit={handleSubmit} className="upload-form" encType="multipart/form-data">
          <div className="form-group">
            <label htmlFor="file">Select File (MP3 or MP4):</label>
            <input 
              type="file" 
              id="file" 
              name="file"
              accept="audio/mp3,audio/mpeg,video/mp4,application/mp4" 
              onChange={handleFileChange} 
              disabled={isUploading}
              required
            />
            {selectedFile && (
              <div className="selected-file">
                Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                <br />
                Type: {selectedFile.type}
                <br />
                Last Modified: {new Date(selectedFile.lastModified).toLocaleString()}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input 
              type="text" 
              id="name" 
              name="name"
              value={fileName} 
              onChange={(e) => setFileName(e.target.value)}
              disabled={isUploading} 
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description (optional):</label>
            <textarea 
              id="description" 
              name="description"
              value={fileDescription} 
              onChange={(e) => setFileDescription(e.target.value)}
              disabled={isUploading}
            />
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="upload-button" 
              disabled={!selectedFile || isUploading || !fileName.trim()}
            >
              {isUploading ? (
                <span>
                  <span className="loading-spinner"></span>
                  Uploading... {selectedFile && `(${formatFileSize(selectedFile.size)})`}
                </span>
              ) : (
                'Upload File'
              )}
            </button>
          </div>
        </form>
        
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}
        {uploadSuccess && (
          <div className="success-message">
            File uploaded successfully!
          </div>
        )}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
      </div>

      {/* Media Files Table */}
      <div className="table-section">
        <h2>Media Files 
          <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
            ({mediaFiles.length} file{mediaFiles.length !== 1 ? 's' : ''})
            {searchTerm && ` • ${filteredMediaFiles.length} matching`}
          </span>
        </h2>
        
        {/* Sorting and resizing instructions */}
        <div style={{ 
          marginBottom: '15px', 
          padding: '10px', 
          backgroundColor: '#e9ecef', 
          borderRadius: '4px',
          fontSize: '14px',
          color: '#495057',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <strong>Table Features:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>Click column headers to sort (↑ ascending, ↓ descending)</li>
              <li>Drag the right edge of column headers to resize</li>
              <li>Hover over truncated text to see full content in tooltip</li>
            </ul>
          </div>
          <button
            onClick={() => {
              // Reset column widths
              setColumnWidths({
                name: 200,
                type: 80,
                size: 100,
                created: 150,
                description: 200,
                id: 120,
                actions: 150
              });
              
              // Reset sorting to default (by date, descending)
              setSortField('createdAt');
              setSortDirection('desc');
              
              logger.info('User reset table to default settings', {
                operation: 'resetTableDefaults',
                resetItems: ['columnWidths', 'sorting'],
                defaultSort: { field: 'createdAt', direction: 'desc' },
                timestamp: new Date().toISOString()
              });
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Reset column widths and sorting to default"
          >
            Reset Table
          </button>
        </div>

        {mediaFiles.length > 0 && !isLoading && (
          <div className="search-container" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              <div style={{ position: 'absolute', left: '10px', color: '#666', zIndex: 1 }}>🔍</div>
              <input 
                ref={searchInputRef}
                type="text"
                placeholder="Search by name, type, size, date, or description... (Ctrl+F)"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  // Track search operation
                  logger.info('User searching media files', {
                    operation: 'searchFiles',
                    searchLength: e.target.value.length,
                    timestamp: new Date().toISOString()
                  });
                }}
                style={{
                  padding: '8px 12px 8px 32px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  boxShadow: searchTerm ? '0 0 0 2px rgba(0,123,255,0.25)' : 'none',
                  width: '100%',
                  maxWidth: '400px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    logger.info('User cleared search', {
                      operation: 'clearSearch',
                      timestamp: new Date().toISOString()
                    });
                  }}
                  style={{
                    marginLeft: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {searchTerm && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                {filteredMediaFiles.length} of {mediaFiles.length} files match your search
              </div>
            )}
          </div>
        )}
        
        {isLoading ? (
          <p>Loading media files...</p>
        ) : mediaFiles.length === 0 ? (
          <p>No media files found. Upload your first file above!</p>
        ) : filteredMediaFiles.length === 0 ? (
          <div className="no-results" style={{ 
            padding: '20px', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '4px',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <p style={{ fontSize: '16px', marginBottom: '10px' }}>No files match your search: <strong>"{searchTerm}"</strong></p>
            <button 
              onClick={() => setSearchTerm('')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear Search
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="media-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <ResizableHeader 
                    columnKey="name" 
                    width={columnWidths.name} 
                    onResize={handleColumnResize} 
                    sortable={true} 
                    sortField="name"
                    sortDirection={sortField === 'name' ? sortDirection : null}
                    onSort={handleSort}
                  >
                    Name
                  </ResizableHeader>
                  <ResizableHeader 
                    columnKey="type" 
                    width={columnWidths.type} 
                    onResize={handleColumnResize} 
                    sortable={true} 
                    sortField="fileType"
                    sortDirection={sortField === 'fileType' ? sortDirection : null}
                    onSort={handleSort}
                  >
                    Type
                  </ResizableHeader>
                  <ResizableHeader 
                    columnKey="size" 
                    width={columnWidths.size} 
                    onResize={handleColumnResize} 
                    sortable={true} 
                    sortField="fileSize"
                    sortDirection={sortField === 'fileSize' ? sortDirection : null}
                    onSort={handleSort}
                  >
                    Size
                  </ResizableHeader>
                  <ResizableHeader 
                    columnKey="created" 
                    width={columnWidths.created} 
                    onResize={handleColumnResize} 
                    sortable={true} 
                    sortField="createdAt"
                    sortDirection={sortField === 'createdAt' ? sortDirection : null}
                    onSort={handleSort}
                  >
                    Created
                  </ResizableHeader>
                  <ResizableHeader 
                    columnKey="description" 
                    width={columnWidths.description} 
                    onResize={handleColumnResize} 
                    sortable={true} 
                    sortField="description"
                    sortDirection={sortField === 'description' ? sortDirection : null}
                    onSort={handleSort}
                  >
                    Description
                  </ResizableHeader>
                  <ResizableHeader 
                    columnKey="id" 
                    width={columnWidths.id} 
                    onResize={handleColumnResize} 
                    sortable={true} 
                    sortField="uniqueId"
                    sortDirection={sortField === 'uniqueId' ? sortDirection : null}
                    onSort={handleSort}
                  >
                    ID
                  </ResizableHeader>
                  <ResizableHeader 
                    columnKey="actions" 
                    width={columnWidths.actions} 
                    onResize={handleColumnResize} 
                    sortable={false}
                  >
                    Actions
                  </ResizableHeader>
                </tr>
              </thead>
              <tbody>
                {filteredMediaFiles.map((file) => (
                  <React.Fragment key={file.uniqueId}>
                    <tr>
                      <td style={{ 
                        width: `${columnWidths.name}px`, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        padding: '8px'
                      }}>
                        <div title={file.name}>
                          {searchTerm ? (
                            // Highlight the matching part of the filename
                            highlightMatchedText(file.name, searchTerm)
                          ) : (
                            file.name
                          )}
                        </div>
                      </td>
                      <td style={{ 
                        width: `${columnWidths.type}px`, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        padding: '8px'
                      }}>
                        {searchTerm ? (
                          highlightMatchedText(file.fileType.toUpperCase(), searchTerm)
                        ) : (
                          file.fileType.toUpperCase()
                        )}
                      </td>
                      <td style={{ 
                        width: `${columnWidths.size}px`, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        padding: '8px'
                      }}>
                        {searchTerm ? (
                          highlightMatchedText(formatFileSize(file.fileSize), searchTerm)
                        ) : (
                          formatFileSize(file.fileSize)
                        )}
                      </td>
                      <td style={{ 
                        width: `${columnWidths.created}px`, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        padding: '8px'
                      }}>
                        <div title={formatDate(file.createdAt)}>
                          {searchTerm ? (
                            highlightMatchedText(formatDate(file.createdAt), searchTerm)
                          ) : (
                            formatDate(file.createdAt)
                          )}
                        </div>
                      </td>
                      <td style={{ 
                        width: `${columnWidths.description}px`, 
                        overflow: 'visible', 
                        padding: '8px',
                        position: 'relative'
                      }}>
                        {editingDescription === file.uniqueId ? (
                          <div className="description-edit-container">
                            <textarea
                              className="description-edit-textarea"
                              value={editDescriptionValue}
                              onChange={(e) => setEditDescriptionValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                  handleSaveDescription(file.uniqueId);
                                } else if (e.key === 'Escape') {
                                  handleCancelEditDescription();
                                }
                              }}
                              placeholder="Enter description..."
                              autoFocus
                            />
                            <div className="description-edit-buttons">
                              <button
                                className="description-edit-save"
                                onClick={() => handleSaveDescription(file.uniqueId)}
                                title="Save (Ctrl+Enter)"
                              >
                                Save
                              </button>
                              <button
                                className="description-edit-cancel"
                                onClick={handleCancelEditDescription}
                                title="Cancel (Escape)"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="description-display">
                            <div 
                              className="description-text"
                              title={file.description || 'No description - Click edit to add one'}
                            >
                              {searchTerm && file.description ? (
                                highlightMatchedText(file.description, searchTerm)
                              ) : (
                                file.description || <em style={{ color: '#999' }}>No description</em>
                              )}
                            </div>
                            <button
                              className="description-edit-button"
                              onClick={() => handleEditDescription(file.uniqueId, file.description)}
                              title="Edit description"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ 
                        width: `${columnWidths.id}px`, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        padding: '8px'
                      }} className="id-column">
                        <div title={file.uniqueId}>{file.uniqueId}</div>
                      </td>
                      <td style={{ 
                        width: `${columnWidths.actions}px`, 
                        overflow: 'visible',
                        padding: '8px'
                      }}>
                        <button 
                          className="view-button" 
                          onClick={() => handleViewFile(file.uniqueId)}
                        >
                          View
                        </button>
                        {canDelete && (
                          <>
                            <button 
                              className="delete-button" 
                              onClick={() => handleDeleteFile(file.uniqueId)}
                            >
                              Delete
                            </button>
                            <button 
                              className="replace-button" 
                              onClick={() => handleReplaceFile(file.uniqueId, file.name)}
                              title="Replace file content while keeping the same ID and QR code"
                            >
                              Replace
                            </button>
                          </>
                        )}
                        <button 
                          className="qr-code-button" 
                          onClick={() => handleDownloadQRCode(file.uniqueId, file.name)}
                        >
                          Download QR Code
                        </button>
                        <button 
                          className="toggle-qr-button" 
                          onClick={() => handleToggleQRCode(file.uniqueId, file.name)}
                          disabled={loadingQRCodes.has(file.uniqueId)}
                        >
                          {loadingQRCodes.has(file.uniqueId) 
                            ? 'Loading...' 
                            : displayedQRCodes.has(file.uniqueId) 
                              ? 'Hide QR Code' 
                              : 'Show QR Code'
                          }
                        </button>
                      </td>
                    </tr>
                    {displayedQRCodes.has(file.uniqueId) && qrCodeDataUrls[file.uniqueId] && (
                      <tr className="qr-code-row">
                        <td colSpan="7" className="qr-code-cell">
                          <div className="qr-code-container">
                            <div className="qr-code-info">
                              <h4>QR Code for {file.name}</h4>
                              <p className="qr-code-description">
                                Scan this QR code to view the media file on any device.
                                <br />
                                <small>Color-coded: {file.fileType.toUpperCase() === 'MP3' || file.fileType.toUpperCase() === 'WAV' || file.fileType.toUpperCase() === 'AAC' || file.fileType.toUpperCase() === 'FLAC' ? 'Blue (Audio)' : file.fileType.toUpperCase() === 'MP4' || file.fileType.toUpperCase() === 'AVI' || file.fileType.toUpperCase() === 'MOV' || file.fileType.toUpperCase() === 'MKV' ? 'Red (Video)' : 'Black/White (Other)'}</small>
                              </p>
                              {qrCodeDataUrls[file.uniqueId].frontendBaseUrl && (
                                <div className="qr-code-url">
                                  <strong>QR Code URL:</strong>
                                  <br />
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <a 
                                      href={`${qrCodeDataUrls[file.uniqueId].frontendBaseUrl.replace(/\/$/, '')}/view-media/${file.uniqueId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="qr-code-link"
                                    >
                                      {`${qrCodeDataUrls[file.uniqueId].frontendBaseUrl.replace(/\/$/, '')}/view-media/${file.uniqueId}`}
                                    </a>
                                    <button
                                      onClick={() => handleCopyUrl(file.uniqueId)}
                                      className="copy-url-button"
                                      title="Copy URL to clipboard"
                                    >
                                      📋 Copy
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="qr-code-image">
                              <img 
                                src={qrCodeDataUrls[file.uniqueId].dataUrl} 
                                alt={`QR Code for ${file.name}`}
                                className="qr-code-img"
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="navigation-buttons">
        <button onClick={handleBackClick} className="back-button">
          Back to Main Page
        </button>
      </div>

      {/* Replace File Dialog */}
      {replaceDialogOpen && (
        <div className="modal-overlay" onClick={() => setReplaceDialogOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Replace File</h2>
            <p>
              You are about to replace: <strong>{replacingFileName}</strong>
            </p>
            <p>
              The file ID and QR code will remain the same, only the file content will be updated.
            </p>
            
            <div className="file-input-container">
              <input
                type="file"
                onChange={(e) => setReplaceFile(e.target.files[0])}
                accept="audio/mp3,audio/mpeg,video/mp4,application/mp4"
                disabled={isReplacing}
              />
              {replaceFile && (
                <div className="file-preview">
                  <strong>Selected file:</strong> {replaceFile.name} ({formatFileSize(replaceFile.size)})
                </div>
              )}
            </div>
            
            {/* Messages in dialog */}
            {error && (
              <div className="error-message" style={{marginTop: '10px', marginBottom: '10px'}}>
                <strong>Error:</strong> {error}
              </div>
            )}
            {successMessage && (
              <div className="success-message" style={{marginTop: '10px', marginBottom: '10px'}}>
                {successMessage}
              </div>
            )}
            
            <div className="modal-actions">
              <button
                onClick={() => {
                  setReplaceDialogOpen(false);
                  setReplacingFileId(null);
                  setReplacingFileName('');
                  setReplaceFile(null);
                  setError(null);
                  setSuccessMessage('');
                }}
                className="cancel-button"
                disabled={isReplacing}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReplace}
                className="confirm-button"
                disabled={!replaceFile || isReplacing}
              >
                {isReplacing ? (
                  <span>
                    <span className="loading-spinner"></span>
                    Replacing...
                  </span>
                ) : 'Replace File'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaFilesPage;
